import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Empty, Input, Select, Spin, Tabs, Tag, Tooltip, Typography } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { userLabel, userSecondary } from '../../lib/listenerName';
import {
  ago,
  CATEGORY_COLOR,
  CATEGORY_LABEL,
  dateTime,
  errMsg,
  STATUS_COLOR,
  STATUS_LABEL,
  ADMIN_SUPPORT,
  listKey,
  type SupportCategory,
  type SupportScope,
  type SupportStatus,
  type SupportSummary,
  type TicketPage,
  type TicketRow,
} from './api';

type Tab = SupportStatus | 'all';

const PAGE_SIZE = 30;

interface Props {
  selectedId: string | null;
  onSelect: (id: string) => void;
  summary: SupportSummary | undefined;
  categories: SupportCategory[];
  scope?: SupportScope;
}

export function TicketList({ selectedId, onSelect, summary, categories, scope = ADMIN_SUPPORT }: Props) {
  const [tab, setTab] = useState<Tab>('open');
  const [category, setCategory] = useState<SupportCategory | undefined>();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  const status = tab === 'all' ? undefined : tab;
  const list = useInfiniteQuery({
    queryKey: [...listKey(scope), { status, category, q }],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<TicketPage>(`${scope.base}/tickets`, {
          params: { status, category, q: q || undefined, before: pageParam, limit: PAGE_SIZE },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    refetchInterval: 20_000,
  });

  // A request can move between pages while they refetch (new activity bumps
  // it to the top), so drop repeats instead of rendering it twice.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: TicketRow[] = [];
    for (const page of list.data?.pages ?? []) {
      for (const t of page.items) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  }, [list.data]);

  const count = (n: number | undefined) => (n ? ` (${n})` : '');

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <div style={{ padding: '4px 16px 12px' }}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as Tab)}
          items={[
            { key: 'open', label: `Open${count(summary?.open)}` },
            { key: 'waiting_user', label: `Waiting for user${count(summary?.waitingUser)}` },
            { key: 'resolved', label: 'Resolved' },
            { key: 'all', label: 'All' },
          ]}
          style={{ marginBottom: 8 }}
        />
        <Input.Search
          allowClear
          placeholder="Name, @handle, request id or user id"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === '') setQ('');
          }}
          onSearch={(v) => setQ(v.trim())}
        />
        <Select<SupportCategory | undefined>
          allowClear
          placeholder="All categories"
          value={category}
          onChange={setCategory}
          style={{ width: '100%', marginTop: 8 }}
          options={categories.map((c) => ({ value: c, label: CATEGORY_LABEL[c] }))}
        />
      </div>

      <div style={{ maxHeight: 'min(70vh, 760px)', overflowY: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {list.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ margin: 16 }}
            message={errMsg(list.error, 'Could not load support requests')}
          />
        ) : list.isLoading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty style={{ padding: 32 }} description="No support requests here" />
        ) : (
          <>
            {rows.map((t) => (
              <TicketListRow
                key={t.id}
                t={t}
                selected={t.id === selectedId}
                showStatus={tab === 'all'}
                onSelect={onSelect}
              />
            ))}
            {list.hasNextPage && (
              <div style={{ padding: 12, textAlign: 'center' }}>
                <Button loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function TicketListRow({
  t,
  selected,
  showStatus,
  onSelect,
}: {
  t: TicketRow;
  selected: boolean;
  showStatus: boolean;
  onSelect: (id: string) => void;
}) {
  const p = t.preview;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onSelect(t.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(t.id);
        }
      }}
      style={{
        cursor: 'pointer',
        padding: '10px 16px 10px 13px',
        borderLeft: `3px solid ${selected ? '#FB3C7F' : 'transparent'}`,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: selected ? 'rgba(251,60,127,0.10)' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            fontWeight: t.unread > 0 ? 700 : 600,
            color: 'white',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {userLabel(t.user)}
        </span>
        {userSecondary(t.user) && (
          <span
            style={{
              fontSize: 12,
              color: '#8B8B8B',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {userSecondary(t.user)}
          </span>
        )}
        <Tag color={CATEGORY_COLOR[t.category]} style={{ marginInlineEnd: 0 }}>
          {CATEGORY_LABEL[t.category]}
        </Tag>
        {t.fromListenerMode && (
          <Tag color="magenta" style={{ marginInlineEnd: 0 }}>
            Listener
          </Tag>
        )}
        <span style={{ flex: 1 }} />
        <Tooltip title={dateTime(t.lastMessageAt)}>
          <span style={{ fontSize: 12, color: '#8B8B8B', whiteSpace: 'nowrap' }}>{ago(t.lastMessageAt)}</span>
        </Tooltip>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
        <Typography.Paragraph
          ellipsis={{ rows: 2 }}
          style={{
            flex: 1,
            margin: 0,
            fontSize: 13,
            color: p?.sender === 'system' ? '#8B8B8B' : '#C9C9CF',
            fontStyle: p?.sender === 'system' ? 'italic' : undefined,
          }}
        >
          {p ? (
            <>
              {p.internal ? (
                <Tag color="warning" icon={<LockOutlined />} style={{ fontSize: 11, marginInlineEnd: 6 }}>
                  Internal
                </Tag>
              ) : p.sender === 'admin' ? (
                <span style={{ color: '#8B8B8B' }}>Support: </span>
              ) : p.sender === 'system' ? (
                <span>Auto: </span>
              ) : null}
              {p.text}
            </>
          ) : (
            <span style={{ color: '#8B8B8B' }}>No messages</span>
          )}
        </Typography.Paragraph>
        {t.unread > 0 && <Badge count={t.unread} size="small" style={{ marginTop: 2 }} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: '#8B8B8B' }}>
        <UserOutlined />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {t.assignedAdmin ? t.assignedAdmin.name : 'Unassigned'}
        </span>
        <span style={{ flex: 1 }} />
        {showStatus && (
          <Tag color={STATUS_COLOR[t.status]} style={{ marginInlineEnd: 0, fontSize: 11 }}>
            {STATUS_LABEL[t.status]}
          </Tag>
        )}
      </div>
    </div>
  );
}
